import React, { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { IconButton, MenuItem, Select } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DashboardMetaLevel1Type, DashboardMetaLevel2Type, Paths } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { LocationForReporting, OrganizationForReporting } from '@/common/types';
import { OrganizationsTable } from '@/components/Organization/OrganizationsTable/OrganizationsTable';
import { Button } from '@/components/common/button/button';
import { resultIsError } from '@/services/HttpService';

interface EditableHeaderProps {
    label: string;
    options: string[];
    onSave: (newLabel: string) => Promise<void>;
}

const EditableHeader = ({ label, options, onSave }: EditableHeaderProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(label);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleSave = async () => {
        await onSave(editValue);
        setIsEditing(false);
    };

    useEffect(() => {
        if (!isEditing) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('.MuiPopover-root')) return;
            setEditValue(label);
            setIsEditing(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isEditing, label]);

    if (isEditing) {
        return (
            <div ref={containerRef} style={{ position: 'relative' }}>
                <div
                    style={{
                        position: 'absolute',
                        top: -8,
                        left: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        zIndex: 1,
                    }}
                >
                    <Select
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value as string)}
                        variant="standard"
                        autoFocus
                        sx={{ minWidth: 100 }}
                    >
                        {options.map((option) => (
                            <MenuItem value={option} key={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                    <IconButton size="small" onClick={handleSave} sx={{ padding: '2px' }}>
                        <CheckIcon fontSize="small" />
                    </IconButton>
                </div>
            </div>
        );
    }

    return (
        <span onClick={() => setIsEditing(true)} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {label}
        </span>
    );
};

interface ReportingLocationsTabProps {
    organizationId: number;
    reportingOrg: OrganizationForReporting | null;
    onReportingOrgChange: (org: OrganizationForReporting) => void;
}

export const ReportingLocationsTab = ({
    organizationId,
    reportingOrg,
    onReportingOrgChange,
}: ReportingLocationsTabProps) => {
    const { httpService } = useHttpContext();
    const navigate = useNavigate();
    const { errorToast, successToast } = toasts;
    const [locations, setLocations] = useState<LocationForReporting[]>([]);

    const getLocations = async () => {
        const res = await httpService.getOrganizationLocationsForReporting(organizationId);
        if (resultIsError(res)) {
            if (res.status === 404) {
                errorToast('Organization does not exist.');
                navigate(Paths.adminReporting);
            } else {
                errorToast('Could not fetch organization locations.');
            }
        } else {
            setLocations(res);
        }
    };

    useEffect(() => {
        (async () => {
            await getLocations();
        })();
    }, [organizationId]);

    const handleEditClick = (locationId: number) => {
        navigate(`${Paths.adminReportingLocations}${locationId}`, {
            state: { organizationId },
        });
    };

    const handleLevelOneLabelSave = async (newLabel: string) => {
        const res = await httpService.updateOrganizationForReporting(organizationId, {
            levelOneLabel: (newLabel as DashboardMetaLevel1Type) || null,
        });
        if (resultIsError(res)) {
            errorToast('Could not update label.');
        } else {
            onReportingOrgChange(res);
            successToast('Label updated successfully.');
        }
    };

    const handleLevelTwoLabelSave = async (newLabel: string) => {
        const res = await httpService.updateOrganizationForReporting(organizationId, {
            levelTwoLabel: (newLabel as DashboardMetaLevel2Type) || null,
        });
        if (resultIsError(res)) {
            errorToast('Could not update label.');
        } else {
            onReportingOrgChange(res);
            successToast('Label updated successfully.');
        }
    };

    const columnStructure = [
        {
            Header: 'Name',
            accessor: 'name',
        },
        {
            Header: 'Onboarding Date',
            accessor: 'onboardingDate',
            Cell: ({ value }) => {
                if (!value) return '—';
                const [year, month, day] = value.split('-');
                return `${month}/${day}/${year}`;
            },
        },
        {
            Header: 'Facility Type',
            accessor: 'facilityType',
            Cell: ({ value }) => value ?? '—',
        },
        {
            Header: () => (
                <EditableHeader
                    label={reportingOrg?.levelOneLabel ?? 'Dashboard Level 1'}
                    options={Object.values(DashboardMetaLevel1Type)}
                    onSave={handleLevelOneLabelSave}
                />
            ),
            accessor: 'dashboardMetaLevel1',
            Cell: ({ value }) => value?.name ?? '—',
        },
        {
            Header: () => (
                <EditableHeader
                    label={reportingOrg?.levelTwoLabel ?? 'Dashboard Level 2'}
                    options={Object.values(DashboardMetaLevel2Type)}
                    onSave={handleLevelTwoLabelSave}
                />
            ),
            accessor: 'dashboardMetaLevel2',
            Cell: ({ value }) => value?.name ?? '—',
        },
        {
            Header: 'Third Party',
            accessor: 'dashboardMetaThirdParty',
            Cell: ({ value }) => value?.name ?? '—',
        },
        {
            Header: 'Hidden',
            accessor: 'hidden',
            Cell: ({ value }) => (value ? 'Yes' : 'No'),
        },
        {
            Header: '',
            id: 'edit',
            accessor: 'id',
            Cell: ({ value }) => (
                <Button onClick={() => handleEditClick(value)} size="small" variant="secondary">
                    Edit
                </Button>
            ),
        },
    ];

    if (locations.length === 0) return null;

    return <OrganizationsTable columnStructure={columnStructure} data={locations} />;
};
